import { safeProductUrl } from '../PhysicalProductSelection/safeProductUrl';
import {
  SamplingChevronDownIcon,
  SamplingExternalLinkIcon,
  SamplingCheckIcon
} from '@/components/common/icon';
import { selectionToApi } from '../PhysicalProductSelection/apiContract';
import {
  SamplingGuideMedia,
  SamplingGuideText
} from '../PhysicalProductSelection/SamplingGuide';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Input, Select } from 'aha-components';
import { getOrderId, isMobile } from '@/biz/tool';
import { getDetailOrderId } from '@/utils/getDetailOrderId';
import { useOrderVisit } from '@/hooks/useOrderVisit';
import { normalizePrice } from '@/utils/normalizePrice';
import { useDetailDataStore } from '@/hooks/useDetailDataStore';
import { creatorOrderV2Api } from '@/apis/creatorOrderV2/creatorOrderV2Api';
import { isCreatorOrderV2ApiError } from '@/apis/creatorOrderV2/errors';
import {
  assertCreatorDeliveryAction,
  handleCreatorDeliveryWriteError
} from '@/biz/multiPlatformCollaboration/creatorDeliveryActions';
import {
  PhysicalSamplingConfig,
  ProductOptionSource,
  ProductSelectionDraft,
  SamplingMode,
  ProductSelectionSubmit,
  submitProductSelection,
  validateProductSelectionDraft,
  hasPersistedProductSelection
} from '../PhysicalProductSelection';
import styles from './ModalAddress.module.less';
import AddressProgress from './AddressProgress';

interface SelectProductContentProps {
  autoFocus: boolean;
  setShowAddressModal: (show: boolean) => void;
  config: PhysicalSamplingConfig;
  draft: ProductSelectionDraft;
  setDraft: React.Dispatch<React.SetStateAction<ProductSelectionDraft>>;
  onContinue: () => void;
  onBack: () => void;
  submitSelection?: ProductSelectionSubmit;
  onGuidePreviewVisibleChange?: (visible: boolean) => void;
}

const SelectProductContent: React.FC<SelectProductContentProps> = ({
  autoFocus,
  setShowAddressModal,
  config,
  draft,
  setDraft,
  onContinue,
  onBack,
  submitSelection,
  onGuidePreviewVisibleChange
}) => {
  const { t } = useTranslation();
  const { detailData, reQueryDetailData } = useDetailDataStore();
  const { order } = detailData;
  const orderId = getOrderId();
  const isCurrentOrderVisit = useOrderVisit(orderId);
  const submitInFlight = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [expandedProducts, setExpandedProducts] = useState<string[]>(() =>
    draft.selectedProductIds.length
      ? [...draft.selectedProductIds]
      : config.products.slice(0, 1).map((product) => product.id)
  );
  const [submissionError, setSubmissionError] = useState('');
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState(false);
  const issues = useMemo(
    () => validateProductSelectionDraft(config, draft),
    [config, draft]
  );
  const selectedCount = draft.selectedProductIds.length;
  const isMultiple = config.mode === SamplingMode.MULTIPLE_PRODUCTS;

  useEffect(() => {
    if (!autoFocus) return;
    const timer = window.setTimeout(() => {
      const firstControl = containerRef.current?.querySelector<HTMLElement>(
        'input:not([type="hidden"]):not([disabled]), button:not([disabled])'
      );
      firstControl?.focus();
    }, 50);
    return () => window.clearTimeout(timer);
  }, [autoFocus]);

  const updateOptionValue = (
    productId: string,
    optionId: string,
    value: string
  ) => {
    setTouched((current) => ({
      ...current,
      [`${productId}:${optionId}`]: true
    }));
    setDraft((current) => ({
      ...current,
      valuesByProductId: {
        ...current.valuesByProductId,
        [productId]: {
          ...current.valuesByProductId[productId],
          productPageSelection:
            current.valuesByProductId[productId]?.productPageSelection ?? '',
          optionSelections: {
            ...current.valuesByProductId[productId]?.optionSelections,
            [optionId]: value
          }
        }
      }
    }));
  };

  const updateProductPageSelection = (productId: string, value: string) => {
    setTouched((current) => ({ ...current, [productId]: true }));
    setDraft((current) => ({
      ...current,
      valuesByProductId: {
        ...current.valuesByProductId,
        [productId]: {
          ...current.valuesByProductId[productId],
          optionSelections:
            current.valuesByProductId[productId]?.optionSelections ?? {},
          productPageSelection: value
        }
      }
    }));
  };

  const toggleProduct = (productId: string) => {
    if (!isMultiple) return;
    setDraft((current) => {
      const isSelected = current.selectedProductIds.includes(productId);
      if (
        !isSelected &&
        current.selectedProductIds.length >= config.maxSelectableProducts
      ) {
        return current;
      }
      return {
        ...current,
        selectedProductIds: isSelected
          ? current.selectedProductIds.filter((id) => id !== productId)
          : [...current.selectedProductIds, productId]
      };
    });
  };

  const handleContinue = async () => {
    if (issues.length || submitting || submitInFlight.current) return;
    if (!isCurrentOrderVisit() || getDetailOrderId(order) !== orderId) return;
    // A saved, unchanged selection can be reviewed again without another write.
    if (hasPersistedProductSelection(config, draft, { order })) {
      onContinue();
      return;
    }
    submitInFlight.current = true;
    setSubmissionError('');
    setSubmitting(true);
    try {
      const result = await submitProductSelection({
        config,
        draft,
        isCurrent: isCurrentOrderVisit,
        submitSelection: async (selection) => {
          assertCreatorDeliveryAction(orderId, 'SUBMIT_ADDRESS');
          if (submitSelection) {
            await submitSelection(selection);
            return;
          }
          await creatorOrderV2Api.productInfo(
            orderId,
            selectionToApi(config, selection)
          );
        },
        submitLegacy: async (skuList) => {
          assertCreatorDeliveryAction(orderId, 'SUBMIT_ADDRESS');
          await creatorOrderV2Api.productInfo(orderId, { skuList });
        },
        refresh: () => reQueryDetailData({ orderId })
      });
      if (!isCurrentOrderVisit() || result === 'STALE') return;
      if (result === 'UNAVAILABLE') {
        setSubmissionError(
          t('physical_product.sampling.unavailable', {
            defaultValue:
              'Product selection is currently unavailable. Please try again later or contact the brand.'
          })
        );
        return;
      }
      if (result !== 'SAVED') {
        setSubmissionError(
          t('physical_product.sampling.refresh_failed', {
            defaultValue:
              'Unable to confirm your saved selection. Please try again.'
          })
        );
        return;
      }
      setSubmissionError('');
      onContinue();
    } catch (error: unknown) {
      if (!isCurrentOrderVisit()) return;
      handleCreatorDeliveryWriteError(error);
      if (
        isCreatorOrderV2ApiError(error) &&
        error.recoveryAction === 'REFRESH_WITHOUT_REPLAY'
      ) {
        await reQueryDetailData({ orderId });
        if (!isCurrentOrderVisit()) return;
        toast.error(
          t('multi_platform.delivery.state_conflict', {
            defaultValue:
              'The collaboration status changed. The latest details have been loaded and nothing was resubmitted.'
          })
        );
        return;
      }
      toast.error(
        (error instanceof Error ? error.message : '') ||
          t('physical_product.sampling.submit_failed', {
            defaultValue: 'Unable to save your product selection.'
          })
      );
    } finally {
      submitInFlight.current = false;
      if (isCurrentOrderVisit()) setSubmitting(false);
    }
  };

  if (config.isUnsupported)
    return (
      <div className="p-6" role="alert">
        {t('physical_product.sampling.unavailable')}
      </div>
    );

  const countIssue = issues.find(
    (issue) => issue.code === 'INVALID_SELECTION_COUNT'
  );
  const mobileStickyFooterClassName =
    'bg-white w-full fixed bottom-0 left-0 right-0 z-[1301] flex flex-col-reverse gap-2 border-t border-solid border-[#e8e8ec] px-4 pt-4 pb-[calc(16px+env(safe-area-inset-bottom,0px))]';

  return (
    <div
      ref={containerRef}
      className={`${isMobile() ? styles.mobileContent : styles.content} ${styles.samplingContent}`}
      style={
        isMobile()
          ? undefined
          : { maxHeight: 'min(810px, calc(100dvh - 96px))' }
      }
    >
      <div
        className={`${styles.scrollBody} ${
          isMobile() ? 'pb-[calc(108px+env(safe-area-inset-bottom,0px))]' : ''
        }`}
      >
        <AddressProgress step={2} hasProductSelection />
        {isMultiple && (
          <div className="mb-4 flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-base font-medium leading-6 text-[#0D0D0D]">
                {t('physical_product.sampling.choose_products', {
                  defaultValue: 'Choose up to {countNum} products',
                  interpolation: { prefix: '{', suffix: '}' },
                  countNum: config.maxSelectableProducts
                })}
              </span>
              <span
                key={selectedCount}
                className="rounded-full border border-solid border-[#FED7AA] bg-[#FFF7ED] px-2 py-0.5 text-xs font-medium leading-[18px] text-[#DF541B]"
              >
                {t('physical_product.sampling.selected_count', {
                  defaultValue: '{selected} selected · Max {available}',
                  interpolation: { prefix: '{', suffix: '}' },
                  selected: selectedCount,
                  available: config.maxSelectableProducts
                })}
              </span>
            </div>
            <span className="text-sm leading-5 text-[#525252]">
              {t('physical_product.sampling.select_count', {
                defaultValue:
                  'Select up to {countNum} from the {total} products offered by the brand.',
                interpolation: { prefix: '{', suffix: '}' },
                countNum: config.maxSelectableProducts,
                total: config.products.length
              })}
            </span>
            {countIssue && selectedCount > 0 && (
              <span className="text-xs leading-4 text-[#D92D20]">
                {t('physical_product.sampling.selection_limit_exceeded', {
                  defaultValue: 'Select no more than {countNum} products.',
                  interpolation: { prefix: '{', suffix: '}' },
                  countNum: config.maxSelectableProducts
                })}
              </span>
            )}
          </div>
        )}

        <div className="flex flex-col gap-3">
          {config.products.map((product) => {
            const selected = draft.selectedProductIds.includes(product.id);
            const expanded = expandedProducts.includes(product.id);
            const selectionLimitReached =
              isMultiple &&
              !selected &&
              selectedCount >= config.maxSelectableProducts;
            const productIssues = issues.filter(
              (issue) => issue.productId === product.id
            );
            return (
              <div
                key={product.id}
                className={`rounded-lg border-0 p-4 transition-colors ${selectionLimitReached ? 'opacity-50' : ''} ${
                  selected
                    ? 'bg-[#FFF7ED] shadow-[inset_0_0_0_1px_#FF7728]'
                    : 'bg-white shadow-[inset_0_0_0_1px_#E5E5E5]'
                }`}
              >
                <div className="flex items-start gap-3">
                  {isMultiple && (
                    <button
                      type="button"
                      role="checkbox"
                      aria-checked={selected}
                      aria-label={product.name}
                      disabled={selectionLimitReached || submitting}
                      className={`mr-1 mt-6 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border border-solid ${selected ? 'border-[#EC470A] bg-[#EC470A]' : 'border-[#D4D4D4] bg-white'} disabled:cursor-not-allowed`}
                      onClick={() => {
                        toggleProduct(product.id);
                        setExpandedProducts((current) =>
                          selected
                            ? current.filter((id) => id !== product.id)
                            : current.includes(product.id)
                              ? current
                              : [...current, product.id]
                        );
                      }}
                    >
                      {selected && <SamplingCheckIcon />}
                    </button>
                  )}
                  <img
                    src={product.imageUrl}
                    alt={product.name}
                    className="h-[68px] w-[68px] shrink-0 rounded-lg border border-solid border-[#E5E5E5] bg-white object-contain p-1.5"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <span
                        title={product.name}
                        className="min-w-0 flex-1 truncate text-base font-semibold leading-6 text-[#171717]"
                      >
                        {product.name}
                      </span>
                      {product.optionSource && (
                        <button
                          type="button"
                          aria-label={t(
                            expanded
                              ? 'submit_history.fold'
                              : 'submit_history.expand',
                            { defaultValue: expanded ? 'Fold' : 'Expand' }
                          )}
                          aria-expanded={expanded}
                          disabled={selectionLimitReached || submitting}
                          className="border-0 bg-transparent p-0"
                          onClick={() =>
                            setExpandedProducts((current) =>
                              expanded
                                ? current.filter((id) => id !== product.id)
                                : [...current, product.id]
                            )
                          }
                        >
                          <SamplingChevronDownIcon
                            className={expanded ? 'rotate-180' : ''}
                          />
                        </button>
                      )}
                    </div>
                    <div className="mt-0.5 text-xs leading-[18px] text-[#737373]">
                      {product.retailPriceUsd === null
                        ? '—'
                        : normalizePrice(product.retailPriceUsd)}
                    </div>
                    <div className="mt-0.5 flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-1 text-sm leading-5 text-[#737373]">
                        {product.brandLogoUrl && (
                          <img
                            src={product.brandLogoUrl}
                            alt=""
                            className="h-4 w-4 rounded object-cover"
                          />
                        )}
                        <span>{product.brandName}</span>
                      </div>
                      {safeProductUrl(product.productUrl) && (
                        <a
                          href={safeProductUrl(product.productUrl) || undefined}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-0.5 text-xs font-medium leading-[18px] text-[#FF621D]"
                        >
                          <span>
                            {t('physical_product.view_product', {
                              defaultValue: 'View product'
                            })}
                          </span>
                          <SamplingExternalLinkIcon />
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                {expanded &&
                  product.optionSource === ProductOptionSource.MANUAL && (
                    <div
                      className={`mt-4 flex flex-col gap-4 rounded-lg p-4 ${selected ? 'bg-white' : 'bg-[#F5F5F5]'}`}
                    >
                      {product.options.map((option, optionIndex) => {
                        const optionIssue =
                          touched[`${product.id}:${option.id}`] &&
                          productIssues.some(
                            (issue) => issue.optionId === option.id
                          );
                        return (
                          <React.Fragment key={option.id}>
                            {optionIndex > 0 && (
                              <div className="relative h-0 shrink-0">
                                <div className="absolute inset-x-0 -top-px border-t border-solid border-[#E5E5E5]" />
                              </div>
                            )}
                            <div className="flex flex-col gap-1.5">
                              <label className="break-words text-sm font-medium leading-5 text-[#292524]">
                                {option.name}{' '}
                                <span className="text-[#D92D20]">*</span>
                              </label>
                              {option.allowedValues.length > 0 ? (
                                <Select
                                  size="mediumSmall"
                                  className={styles.samplingSelect}
                                  style={{
                                    fontFamily:
                                      'Sampling Inter, Inter, sans-serif',
                                    paddingTop: 7,
                                    paddingBottom: 7
                                  }}
                                  value={
                                    draft.valuesByProductId[product.id]
                                      ?.optionSelections[option.id] || undefined
                                  }
                                  options={option.allowedValues.map(
                                    (value) => ({
                                      label: value,
                                      value
                                    })
                                  )}
                                  placeholder={t(
                                    'physical_product.sampling.select_option_named',
                                    {
                                      defaultValue: 'Select {{optionName}}',
                                      optionName:
                                        option.name.toLocaleLowerCase()
                                    }
                                  )}
                                  dropdownStyle={{ zIndex: 10000 }}
                                  error={optionIssue}
                                  onChange={(value) =>
                                    updateOptionValue(
                                      product.id,
                                      option.id,
                                      String(value || '')
                                    )
                                  }
                                />
                              ) : (
                                <Input
                                  value={
                                    draft.valuesByProductId[product.id]
                                      ?.optionSelections[option.id] || ''
                                  }
                                  placeholder={t(
                                    'physical_product.sampling.enter_option',
                                    { defaultValue: 'Enter your option' }
                                  )}
                                  onChange={(event) =>
                                    updateOptionValue(
                                      product.id,
                                      option.id,
                                      event.target.value
                                    )
                                  }
                                />
                              )}
                              {option.selectionGuide && (
                                <div className="text-xs font-normal leading-[18px] text-[#525252]">
                                  <SamplingGuideText
                                    value={option.selectionGuide}
                                    format={option.selectionGuideFormat}
                                  />
                                </div>
                              )}
                              {optionIssue && (
                                <div className="text-xs leading-4 text-[#D92D20]">
                                  {t(
                                    'physical_product.sampling.option_required',
                                    {
                                      defaultValue:
                                        'Please select a product option.'
                                    }
                                  )}
                                </div>
                              )}
                              {option.selectionGuideImages.length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                  <SamplingGuideMedia
                                    items={option.selectionGuideImages}
                                    imageSize={32}
                                    gap={4}
                                    previewZIndex={1600}
                                    onPreviewVisibleChange={
                                      onGuidePreviewVisibleChange
                                    }
                                  />
                                </div>
                              )}
                            </div>
                          </React.Fragment>
                        );
                      })}
                    </div>
                  )}

                {expanded &&
                  product.optionSource === ProductOptionSource.PRODUCT_PAGE && (
                    <div
                      className={`mt-4 flex flex-col gap-2 rounded-lg p-4 ${selected ? 'bg-white' : 'bg-[#F5F5F5]'}`}
                    >
                      {safeProductUrl(product.productUrl) && (
                        <div className="flex flex-col gap-1.5">
                          <span className="text-sm font-medium leading-5 text-[#404040]">
                            {t('physical_product.sampling.product_link', {
                              defaultValue: 'Product link'
                            })}
                          </span>
                          <a
                            href={
                              safeProductUrl(product.productUrl) || undefined
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="truncate text-sm leading-5 text-[#2563EB]"
                          >
                            {product.productUrl}
                          </a>
                        </div>
                      )}
                      <label className="mt-1 text-sm font-medium leading-5 text-[#292524]">
                        {t('physical_product.sampling.your_selection', {
                          defaultValue: 'Your selection'
                        })}{' '}
                        <span className="text-[#D92D20]">*</span>
                      </label>
                      <Input
                        value={
                          draft.valuesByProductId[product.id]
                            ?.productPageSelection || ''
                        }
                        placeholder={t(
                          'physical_product.sampling.your_selection_placeholder',
                          { defaultValue: 'Enter your product selection' }
                        )}
                        maxLength={1000}
                        onChange={(event) =>
                          updateProductPageSelection(
                            product.id,
                            event.target.value
                          )
                        }
                      />
                      {product.productPageSelectionGuide && (
                        <div className="text-xs leading-[18px] text-[#525252]">
                          <SamplingGuideText
                            value={product.productPageSelectionGuide}
                            format={product.productPageSelectionGuideFormat}
                          />
                        </div>
                      )}
                      <SamplingGuideMedia
                        items={product.productPageSelectionGuideImages}
                        imageSize={32}
                        gap={4}
                        previewZIndex={1600}
                        onPreviewVisibleChange={onGuidePreviewVisibleChange}
                      />
                      {touched[product.id] &&
                        productIssues.some(
                          (issue) =>
                            issue.code === 'REQUIRED_PRODUCT_PAGE_SELECTION'
                        ) && (
                          <div className="text-xs leading-4 text-[#D92D20]">
                            {t(
                              'physical_product.sampling.product_page_selection_required',
                              {
                                defaultValue:
                                  'Please enter your product selection.'
                              }
                            )}
                          </div>
                        )}
                    </div>
                  )}
              </div>
            );
          })}
        </div>
      </div>

      {submissionError && (
        <div role="alert" className="mb-3 text-sm leading-5 text-[#D92D20]">
          <span key={submissionError}>{submissionError}</span>
        </div>
      )}
      <div
        className={`${
          isMobile()
            ? mobileStickyFooterClassName
            : `${styles.selectionFooter} grid grid-cols-3 gap-2`
        }`}
      >
        <button
          type="button"
          disabled={submitting}
          className={`${styles.samplingButton} ${styles.secondaryButton} disabled:opacity-50`}
          onClick={onBack}
        >
          {t('common.modal.back_btn', { defaultValue: 'Back' })}
        </button>
        <button
          type="button"
          disabled={submitting}
          className={`${styles.samplingButton} ${styles.secondaryButton} ${isMobile() ? 'w-full' : ''}`}
          onClick={() => setShowAddressModal(false)}
        >
          {t('modal.address.cancel', { defaultValue: 'Cancel' })}
        </button>
        <button
          type="button"
          disabled={issues.length > 0 || submitting}
          className={`${styles.samplingButton} ${styles.primaryButton} ${isMobile() ? 'w-full' : ''} disabled:cursor-not-allowed disabled:opacity-50`}
          onClick={handleContinue}
        >
          <span key={submitting ? 'saving' : 'continue'}>
            {submitting
              ? t('physical_product.sampling.saving', {
                  defaultValue: 'Saving...'
                })
              : t('modal.address.continue', { defaultValue: 'Continue' })}
          </span>
        </button>
      </div>
    </div>
  );
};

export default SelectProductContent;
